import FormGroupWithInput from '@reverbdotcom/commons/src/components/form_group_with_input';
import { withGraphql } from '@reverbdotcom/commons/src/with_graphql';
import gql from 'graphql-tag';
import { debounce } from 'lodash';
import React from 'react';
import { ChildProps } from 'react-apollo';
import { SearchUsers } from '../schemas/rql';

export const SEARCH_USERS_QUERY = gql`
  query SEARCH_USERS(
    $input: Input_core_apimessages_AdminUserSearchRequest
  ) {
    userSearch(input: $input) {
      users {
        id
        email
        shopId
        shopName
      }
    }
  }
`;

interface IProps {
  searchText: string;
  excludedUsers: SearchUsers.Users[];
  userSelected: (user: any) => void;
}

type Props = ChildProps<IProps, SearchUsers.Query>;

const withSearchUsersQuery = withGraphql<IProps, SearchUsers.Query>(SEARCH_USERS_QUERY, {
  options: (ownProps) => {
    return {
      errorPolicy: 'all',
      ssr: false, // admin only
      variables: {
        input: {
          search: ownProps.searchText,
        },
      },
    };
  },
});

export default function AdminDisbursementShopExclusionFormFields() {
  const [searchText, setSearchText] = React.useState('');
  const [excludedUsers, setExcludedUsers] = React.useState<SearchUsers.Users[]>([]);
  const [debouncedSearchText, setDebouncedSearchText] = React.useState(searchText);

  const onUpdateSearchText = (field) => {
    setSearchText(field.search);
  };

  const debounceSearchText = React.useCallback(debounce((newSearchText) => {
    setDebouncedSearchText(newSearchText);
  }, 500), []);

  const onUserSelected = (user: SearchUsers.Users) => {
    if (!user.shopId) {
      return;
    }
    setExcludedUsers(excludedUsers.concat(user));
  };

  const removeUserFromExclusionList = (user: SearchUsers.Users) => {
    setExcludedUsers(excludedUsers.filter((u) => u.id !== user.id));
  };

  React.useEffect(() => {
    debounceSearchText(searchText);
  }, [searchText, debounceSearchText]);

  return (
    <>
      <h4>Want to exclude shops from this disbursement? Search to exclude them here.</h4>
      <div className="g-container">
        <div className="g-col-6">
          <FormGroupWithInput
            label=""
            placeholder=""
            inputName="search"
            value={searchText}
            updateField={onUpdateSearchText}
          />
        </div>
      </div>
      {
        debouncedSearchText &&
        <div className="ptb-4">
          <AdminDisbursementShopSearchResults
            searchText={debouncedSearchText}
            userSelected={onUserSelected}
            excludedUsers={excludedUsers}
          />
        </div>
      }
      { excludedUsers.length > 0 &&
        <>
          <h3>Users Excluded from Disbursements</h3>
          <div className="styled-table">
            <table>
              <tbody>
                {excludedUsers.map((user) => {
                  return (
                    <tr key={user.id}>
                      <td>
                        <span>{user.email} - {user.shopName}</span>
                        <input type="hidden" name="excluded_shops[]" value={user.shopId}></input>
                      </td>
                      <td>
                        <button
                          type="button"
                          className="button-as-link"
                          onClick={() => removeUserFromExclusionList(user)}
                        >
                          Remove user from exclusion list
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      }
    </>
  );
}

function AdminDisbursementShopSearchResultsDisplay({ excludedUsers, userSelected, data }: Props) {
  if (data.loading) {
    return (<p>Loading...</p>);
  }

  const usersToDisplay = (data.userSearch?.users || []).filter(u => excludedUsers.every(eu => eu.id !== u.id));

  if (!usersToDisplay.length) {
    return <p>No shops found to exclude.  Please search again!</p>;
  }

  return (
    <div className="styled-table">
      <table>
        <tbody>
          {usersToDisplay.map((user) => {
            return (
              <tr key={user.id}>
                <td>
                  {user.email} - {user.shopName}
                </td>
                <td>
                  <button
                    type="button"
                    className="button-as-link"
                    onClick={() => userSelected(user)}
                  >
                    Exclude User
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

const AdminDisbursementShopSearchResults = withSearchUsersQuery(AdminDisbursementShopSearchResultsDisplay);
